import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowRight, Heart } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/auth";
import { LANDING } from "../constants/testIds";
import { toast } from "sonner";
import BtcLogo from "./BtcLogo";

// Every footer link either points to a real route or is intentionally
// non-interactive (rendered as inert text). No dead href="#" links.

const columns = [
  {
    title: "BTC",
    links: [
      { label: "Home", to: "/" },
      { label: "About BTC", to: "/about" },
      { label: "Get Involved", to: "/get-involved" },
      { label: "Portal Preview", to: "/portal-preview" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Stories", to: "/stories" },
      { label: "Events", to: "/events" },
      { label: "Volunteer", to: "/get-involved" },
      { label: "Become a Buddy", to: "/buddy/apply" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Get Support", to: "/support" },
      { label: "Contact", to: "/contact" },
      { label: "Donate", to: "/donate" },
    ],
  },
  {
    title: "Account",
    links: [
      { label: "Log in", to: "/login" },
      { label: "Join BTC", to: "/register" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/forms/newsletter", { email });
      toast.success("You're subscribed. Welcome to the BTC community.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid={LANDING.footer} className="bg-[#F8F9FB] border-t border-gray-200">
      <div className="btc-container py-20">
        {/* Top: newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-14 border-b border-gray-200">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-[#23803F]" fill="#2D9B4E" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#23803F]">
                Stay Connected
              </span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-[#0B132B] tracking-tight mb-3">
              No one walks this journey alone.
            </h3>
            <p className="text-[#4B5563] text-base max-w-md">
              Subscribe for monthly stories of hope, resources, and updates from our global community.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-10 flex items-center">
            <form onSubmit={submitNewsletter} className="w-full flex flex-col sm:flex-row gap-3" aria-label="Subscribe to the BTC newsletter">
              <label htmlFor="btc-footer-newsletter-email" className="sr-only">
                Email address for the BTC newsletter
              </label>
              <input
                id="btc-footer-newsletter-email"
                data-testid={LANDING.newsletterInput}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                aria-label="Email address for the BTC newsletter"
                className="flex-1 px-5 py-4 rounded-full bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/20 text-[#0B132B] placeholder:text-gray-400"
              />
              <button
                type="submit"
                data-testid={LANDING.newsletterButton}
                disabled={submitting}
                className="btc-btn-primary justify-center disabled:opacity-60"
              >
                {submitting ? "Subscribing..." : "Subscribe"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Columns — real routes only */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#0B132B] mb-4">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-[#4B5563] hover:text-[#1A5FAB] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="pt-10 border-t border-gray-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Link to="/" className="inline-flex items-center gap-3 mb-3">
              <BtcLogo className="h-16 w-auto" />
            </Link>
            <p className="text-xs text-[#4B5563] leading-relaxed">
              © {new Date().getFullYear()} Bonded Thru Cancer. All rights reserved.
              <br />
              <span className="font-semibold text-[#0B132B]">United Kingdom Headquarters</span> · Serving Patients Worldwide.
              <br />
              The world's first Patient Portal Ecosystem for the cancer community.
            </p>
          </div>
          {/* Social icons — non-interactive placeholders (no real accounts wired yet). */}
          <div className="flex items-center gap-3" aria-hidden="true">
            {[Facebook, Twitter, Instagram, Linkedin, Youtube].map((Icon, i) => (
              <span
                key={i}
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#4B5563]/60"
              >
                <Icon className="w-4 h-4" />
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
